import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Info } from 'lucide-react';
import type { TextbookCatalogNode, UnitSummary } from '@en3/shared';
import { contentApi } from '@/api/content.api';
import { queryKeys } from '@/api/client';
import { isApiError, toDisplayMessage } from '@/api/errors';
import { useAuthStatus } from '@/api/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { FormSelect, FormTextarea } from './ArticleCard';

/**
 * 新建单元（教师 / 管理员）。
 *
 * 两个"看起来小、但不做就会出错"的细节：
 *
 * 1. **单元号默认取该教材现有最大号 + 1。** 数据库对 `(textbookId, unitNo)` 有唯一约束，
 *    手工建单元时最常犯的错就是重复号。这里读一次已有单元算出下一个可用号，
 *    但仍然允许用户改（教师有时要先留出位置）。
 *
 * 2. **教材可选、且默认沿用目录页当前那本。** 在教材卡片里点「新建单元」时，
 *    用户心里的主语就是那一本，让他再选一次是负担。
 *
 * 单元标题允许先写 `Unit N` 占位：种子目录里大量单元标题查不到公开出处，
 * 平台的原则是"宁可留空也不编造"，但也要让教师能用占位号先把结构立起来。
 */

export interface CreateUnitDialogProps {
  open: boolean;
  onClose: () => void;
  /** 目录页已有的教材（含单元），用于选教材与算下一个单元号 */
  textbooks: TextbookCatalogNode[];
  /** 预选教材（在某个教材卡片里点"新建单元"时传入） */
  defaultTextbookId?: string | undefined;
  /** 创建成功后由页面失效目录；可选地直接打开导入对话框并预选这个新单元 */
  onCreated: (unit: UnitSummary, textbookId: string) => void;
}

interface FormState {
  textbookId: string;
  unitNo: string;
  title: string;
  titleZh: string;
  theme: string;
  summary: string;
}

export function CreateUnitDialog({
  open,
  onClose,
  textbooks,
  defaultTextbookId,
  onCreated,
}: CreateUnitDialogProps) {
  const status = useAuthStatus();
  const queryClient = useQueryClient();
  const [form, setForm] = useState<FormState>({
    textbookId: '',
    unitNo: '1',
    title: '',
    titleZh: '',
    theme: '',
    summary: '',
  });
  const [error, setError] = useState<string | null>(null);

  const selectedTextbookId = form.textbookId;

  /** 目录页已经带了 units，直接复用；万一没带才按需拉一次。 */
  const embeddedUnits = useMemo(
    () => textbooks.find((item) => item.id === selectedTextbookId)?.units,
    [textbooks, selectedTextbookId],
  );

  const unitsQuery = useQuery({
    queryKey: queryKeys.textbook(selectedTextbookId),
    queryFn: () => contentApi.units(selectedTextbookId),
    enabled: open && status === 'authenticated' && selectedTextbookId !== '' && embeddedUnits === undefined,
  });

  const existingUnits: UnitSummary[] | undefined = embeddedUnits ?? unitsQuery.data?.items;

  // 打开时重置并把教材预选上；单元号等已有单元读回来之后再填（见下面的 effect）。
  //
  // 用 ref 记住"上一次是不是开着的"，这样 textbooks 变化（目录刷新会生成新数组）
  // 不会把用户正在填的表单清空。
  const wasOpen = useRef(false);
  /** 已经自动编过号的教材 id：换教材才重算，避免把用户手改的编号覆盖掉 */
  const numberedFor = useRef<string | null>(null);

  useEffect(() => {
    if (open && !wasOpen.current) {
      setError(null);
      numberedFor.current = null; // 重新打开时允许按最新数据重算一次编号
      setForm({
        textbookId: defaultTextbookId ?? textbooks[0]?.id ?? '',
        unitNo: '1',
        title: '',
        titleZh: '',
        theme: '',
        summary: '',
      });
    }
    wasOpen.current = open;
  }, [open, defaultTextbookId, textbooks]);

  /**
   * 单元号自动回填。
   *
   * 只在"教材换了 + 已有单元数据到位"时算一次下一个可用号，之后不再覆盖用户输入：
   * 每次数据变化都重算的话，用户手改成 10 之后会被悄悄改回 9，这比默认值不准更糟。
   */
  useEffect(() => {
    if (existingUnits === undefined || selectedTextbookId === '') return;
    if (numberedFor.current === selectedTextbookId) return;
    numberedFor.current = selectedTextbookId;
    const nextNo = existingUnits.reduce((max, unit) => Math.max(max, unit.unitNo), 0) + 1;
    setForm((current) => (current.textbookId === selectedTextbookId ? { ...current, unitNo: String(nextNo) } : current));
  }, [existingUnits, selectedTextbookId]);

  const createMutation = useMutation({
    mutationFn: ({ textbookId, input }: { textbookId: string; input: Record<string, unknown> }) =>
      contentApi.createUnit(textbookId, input),
  });

  function patch(next: Partial<FormState>) {
    setForm((current) => ({ ...current, ...next }));
  }

  const unitNo = Number(form.unitNo);
  const unitNoValid = Number.isInteger(unitNo) && unitNo >= 1 && unitNo <= 999;
  const duplicate = existingUnits?.some((unit) => unit.unitNo === unitNo) ?? false;
  const canSubmit =
    status === 'authenticated' &&
    form.textbookId !== '' &&
    form.title.trim().length > 0 &&
    unitNoValid &&
    !duplicate;

  async function submit() {
    setError(null);
    try {
      const data = await createMutation.mutateAsync({
        textbookId: form.textbookId,
        input: {
          unitNo,
          title: form.title.trim(),
          ...(form.titleZh.trim() ? { titleZh: form.titleZh.trim() } : {}),
          ...(form.theme.trim() ? { theme: form.theme.trim() } : {}),
          ...(form.summary.trim() ? { summary: form.summary.trim() } : {}),
        },
      });
      // 目录树里多了一个单元，必须刷新；否则用户看不到刚建的那个空单元。
      await queryClient.invalidateQueries({ queryKey: ['textbooks'] });
      onCreated(data.unit, form.textbookId);
    } catch (cause) {
      const fieldIssue = isApiError(cause) ? cause.fieldIssues[0] : undefined;
      setError(fieldIssue ? fieldIssue.message : toDisplayMessage(cause));
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      variant="sheet"
      title="新建单元"
      className="sm:max-w-xl"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            取消
          </Button>
          <Button onClick={() => void submit()} disabled={!canSubmit} loading={createMutation.isPending}>
            创建单元
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field id="unit-textbook" label="所属教材" hint="单元只在某一本教材内部编号">
            <FormSelect
              id="unit-textbook"
              value={form.textbookId}
              onChange={(event) => patch({ textbookId: event.target.value, unitNo: '1' })}
              className="w-full"
            >
              <option value="">请选择教材</option>
              {textbooks.map((textbook) => (
                <option key={textbook.id} value={textbook.id}>
                  {textbook.title}
                </option>
              ))}
            </FormSelect>
          </Field>

          <Field
            id="unit-no"
            label="单元号"
            hint={duplicate ? undefined : '默认填下一个可用编号'}
            error={duplicate ? `这本教材里已经有 Unit ${unitNo} 了，请换一个编号` : undefined}
          >
            <Input
              id="unit-no"
              type="number"
              min={1}
              max={999}
              value={form.unitNo}
              aria-invalid={!unitNoValid || duplicate}
              onChange={(event) => patch({ unitNo: event.target.value })}
            />
          </Field>

          <Field id="unit-title" label="单元标题" hint="必填；查不到公开标题时先写 Unit N 占位也可以">
            <Input
              id="unit-title"
              value={form.title}
              onChange={(event) => patch({ title: event.target.value })}
              placeholder="例如：The way to success"
            />
          </Field>

          <Field id="unit-title-zh" label="中文标题" hint="可选">
            <Input
              id="unit-title-zh"
              value={form.titleZh}
              onChange={(event) => patch({ titleZh: event.target.value })}
            />
          </Field>

          <Field id="unit-theme" label="主题" hint="可选，用于筛选与推荐，例如：成功与个人成长">
            <Input id="unit-theme" value={form.theme} onChange={(event) => patch({ theme: event.target.value })} />
          </Field>
        </div>

        <Field id="unit-summary" label="单元说明" hint="可选，一句话说明这个单元练什么；不要写入课文内容">
          <FormTextarea
            id="unit-summary"
            rows={3}
            value={form.summary}
            onChange={(event) => patch({ summary: event.target.value })}
          />
        </Field>

        <p className="flex items-start gap-1.5 rounded-md bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
          <Info aria-hidden className="mt-0.5 size-3.5 shrink-0" />
          单元说明属于公开目录信息。请勿把课文正文、习题或译文写进标题与说明里 ——
          正文请通过「导入文章」单独导入并留下授权记录。
        </p>

        {error && (
          <p
            role="alert"
            className="flex items-start gap-1.5 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive"
          >
            <AlertTriangle aria-hidden className="mt-0.5 size-4 shrink-0" />
            {error}
          </p>
        )}
      </div>
    </Dialog>
  );
}
